export type CanvasShortcutId =
  | "new-document"
  | "new-note"
  | "new-date-event"
  | "new-arrow"
  | "new-frame"
  | "new-folder"
  | "new-web-clip"
  | "new-media"
  | "new-label"
  | "open"
  | "select-all"
  | "undo"
  | "redo"
  | "copy"
  | "paste"
  | "duplicate"
  | "bring-front"
  | "send-back"
  | "delete"
  | "fit"
  | "text-bold"
  | "text-italic"
  | "text-underline"
  | "text-paragraph"
  | "text-heading-one"
  | "text-heading-two"
  | "text-heading-three"
  | "text-heading-four"
  | "text-heading-five"
  | "text-align-left"
  | "text-align-center"
  | "text-align-right"
  | "text-align-justify"
  | "text-bullet-list"
  | "text-ordered-list"
  | "text-checklist"
  | "text-quote"
  | "text-code-block"
  | "text-highlight";

export type ShortcutPlatform = "mac" | "other";

export interface CanvasShortcutBinding {
  command: boolean;
  control?: boolean;
  shift: boolean;
  alt: boolean;
  altOrCommand?: boolean;
  code: string;
  keys: readonly string[];
  displayKey: string;
  macDisplayKey?: string;
  ariaKeys?: readonly string[];
  electronKey?: string;
}

export type CanvasShortcutBindings = Record<CanvasShortcutId, CanvasShortcutBinding>;
export type CanvasShortcutAccelerators = Partial<Record<CanvasShortcutId, string | null>>;

export const CANVAS_SHORTCUT_STORAGE_KEY = "bedrock-canvas-shortcuts-v1";

export const SYSTEM_CLIPBOARD_PASTE_ACTION = {
  group: "Edit",
  label: "Paste from OS",
  description: "Paste links or supported files from the OS clipboard · fixed"
} as const;

export const CANVAS_SHORTCUT_ACTIONS: ReadonlyArray<{
  id: CanvasShortcutId;
  group: "Create" | "Edit" | "Arrange" | "View" | "Text";
  label: string;
  description: string;
}> = [
  { id: "new-document", group: "Create", label: "New document", description: "Create a document at the center of the canvas" },
  { id: "new-note", group: "Create", label: "New note", description: "Create a note at the center of the canvas" },
  { id: "new-date-event", group: "Create", label: "New event", description: "Set a named date, with or without a time, at the center of the canvas" },
  { id: "new-arrow", group: "Create", label: "New arrow", description: "Create an arrow at the center of the canvas" },
  { id: "new-frame", group: "Create", label: "New frame", description: "Create a frame at the center of the canvas" },
  { id: "new-folder", group: "Create", label: "New folder", description: "Create a folder at the center of the canvas" },
  { id: "new-web-clip", group: "Create", label: "New web clip", description: "Open the web clip entry" },
  { id: "new-media", group: "Create", label: "Add file", description: "Choose an image, video, PDF, or Excel workbook" },
  { id: "new-label", group: "Create", label: "New label", description: "Create a label at the center of the canvas" },
  { id: "open", group: "Edit", label: "Open selected item", description: "Open the selected item or folder" },
  { id: "select-all", group: "Edit", label: "Select all", description: "Select every item in the current space" },
  { id: "undo", group: "Edit", label: "Undo", description: "Undo the latest canvas change" },
  { id: "redo", group: "Edit", label: "Redo", description: "Redo the latest canvas change" },
  { id: "copy", group: "Edit", label: "Copy", description: "Copy the selected canvas items" },
  { id: "paste", group: "Edit", label: "Paste canvas items", description: "Paste copied canvas items" },
  { id: "duplicate", group: "Edit", label: "Duplicate", description: "Create a copy of the selected items" },
  { id: "delete", group: "Edit", label: "Move to Trash", description: "Move the selected items to Trash" },
  { id: "bring-front", group: "Arrange", label: "Bring to front", description: "Place selected items above the others" },
  { id: "send-back", group: "Arrange", label: "Send to back", description: "Place selected items behind the others" },
  { id: "fit", group: "View", label: "Fit canvas", description: "Center the canvas on its visible items" },
  { id: "text-bold", group: "Text", label: "Bold", description: "Toggle bold text" },
  { id: "text-italic", group: "Text", label: "Italic", description: "Toggle italic text" },
  { id: "text-underline", group: "Text", label: "Underline", description: "Toggle underlined text" },
  { id: "text-paragraph", group: "Text", label: "Paragraph", description: "Use regular paragraph text" },
  { id: "text-heading-one", group: "Text", label: "Heading 1", description: "Toggle a first-level heading" },
  { id: "text-heading-two", group: "Text", label: "Heading 2", description: "Toggle a second-level heading" },
  { id: "text-heading-three", group: "Text", label: "Heading 3", description: "Toggle a third-level heading" },
  { id: "text-heading-four", group: "Text", label: "Heading 4", description: "Toggle a fourth-level heading" },
  { id: "text-heading-five", group: "Text", label: "Heading 5", description: "Toggle a fifth-level heading" },
  { id: "text-align-left", group: "Text", label: "Align left", description: "Align text to the left" },
  { id: "text-align-center", group: "Text", label: "Align center", description: "Center text" },
  { id: "text-align-right", group: "Text", label: "Align right", description: "Align text to the right" },
  { id: "text-align-justify", group: "Text", label: "Justify", description: "Justify text" },
  { id: "text-bullet-list", group: "Text", label: "Bulleted list", description: "Toggle a bulleted list" },
  { id: "text-ordered-list", group: "Text", label: "Numbered list", description: "Toggle a numbered list" },
  { id: "text-checklist", group: "Text", label: "Checklist", description: "Toggle a checklist" },
  { id: "text-quote", group: "Text", label: "Quote", description: "Toggle a block quote" },
  { id: "text-code-block", group: "Text", label: "Code block", description: "Toggle a code block" },
  { id: "text-highlight", group: "Text", label: "Highlight", description: "Toggle highlighted text" }
];

type ShortcutEvent = Pick<KeyboardEvent, "altKey" | "code" | "ctrlKey" | "key" | "metaKey" | "shiftKey">;

const defaultBindings: CanvasShortcutBindings = {
  "new-document": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyD", keys: ["d"], displayKey: "D" },
  "new-note": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyN", keys: ["n"], displayKey: "N" },
  "new-date-event": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyE", keys: ["e"], displayKey: "E" },
  "new-arrow": { command: false, shift: true, alt: false, altOrCommand: true, code: "KeyA", keys: ["a"], displayKey: "A" },
  "new-frame": { command: false, shift: true, alt: false, altOrCommand: true, code: "KeyF", keys: ["f"], displayKey: "F" },
  "new-folder": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyF", keys: ["f"], displayKey: "F" },
  "new-web-clip": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyK", keys: ["k"], displayKey: "K" },
  "new-media": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyM", keys: ["m"], displayKey: "M" },
  "new-label": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyL", keys: ["l"], displayKey: "L" },
  open: { command: false, shift: false, alt: false, code: "Enter", keys: ["enter"], displayKey: "Enter" },
  "select-all": { command: true, shift: false, alt: false, code: "KeyA", keys: ["a"], displayKey: "A" },
  undo: { command: true, shift: false, alt: false, code: "KeyZ", keys: ["z"], displayKey: "Z" },
  redo: { command: true, shift: true, alt: false, code: "KeyZ", keys: ["z"], displayKey: "Z" },
  copy: { command: true, shift: false, alt: false, code: "KeyC", keys: ["c"], displayKey: "C" },
  paste: { command: true, shift: false, alt: false, code: "KeyV", keys: ["v"], displayKey: "V" },
  duplicate: { command: false, control: true, shift: false, alt: false, code: "KeyD", keys: ["d"], displayKey: "D" },
  "bring-front": { command: false, shift: false, alt: false, altOrCommand: true, code: "ArrowUp", keys: ["arrowup"], displayKey: "↑" },
  "send-back": { command: false, shift: false, alt: false, altOrCommand: true, code: "ArrowDown", keys: ["arrowdown"], displayKey: "↓" },
  delete: { command: false, shift: false, alt: false, code: "Delete", keys: ["delete", "backspace"], displayKey: "Del", macDisplayKey: "⌫", ariaKeys: ["Delete", "Backspace"], electronKey: "Backspace" },
  fit: { command: false, shift: true, alt: false, altOrCommand: true, code: "Digit1", keys: ["1", "!"], displayKey: "1" },
  "text-bold": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyB", keys: ["b"], displayKey: "B" },
  "text-italic": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyI", keys: ["i"], displayKey: "I" },
  "text-underline": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyU", keys: ["u"], displayKey: "U" },
  "text-paragraph": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyP", keys: ["p"], displayKey: "P" },
  "text-heading-one": { command: false, shift: false, alt: false, altOrCommand: true, code: "Digit1", keys: ["1"], displayKey: "1" },
  "text-heading-two": { command: false, shift: false, alt: false, altOrCommand: true, code: "Digit2", keys: ["2"], displayKey: "2" },
  "text-heading-three": { command: false, shift: false, alt: false, altOrCommand: true, code: "Digit3", keys: ["3"], displayKey: "3" },
  "text-heading-four": { command: false, shift: false, alt: false, altOrCommand: true, code: "Digit4", keys: ["4"], displayKey: "4" },
  "text-heading-five": { command: false, shift: false, alt: false, altOrCommand: true, code: "Digit5", keys: ["5"], displayKey: "5" },
  "text-align-left": { command: true, shift: true, alt: false, code: "KeyL", keys: ["l"], displayKey: "L" },
  "text-align-center": { command: true, shift: true, alt: false, code: "KeyE", keys: ["e"], displayKey: "E" },
  "text-align-right": { command: true, shift: true, alt: false, code: "KeyR", keys: ["r"], displayKey: "R" },
  "text-align-justify": { command: true, shift: true, alt: false, code: "KeyJ", keys: ["j"], displayKey: "J" },
  "text-bullet-list": { command: false, shift: true, alt: false, altOrCommand: true, code: "KeyB", keys: ["b"], displayKey: "B" },
  "text-ordered-list": { command: false, shift: true, alt: false, altOrCommand: true, code: "KeyN", keys: ["n"], displayKey: "N" },
  "text-checklist": { command: false, shift: true, alt: false, altOrCommand: true, code: "KeyC", keys: ["c"], displayKey: "C" },
  "text-quote": { command: false, shift: true, alt: false, altOrCommand: true, code: "KeyU", keys: ["u"], displayKey: "U" },
  "text-code-block": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyY", keys: ["y"], displayKey: "Y" },
  "text-highlight": { command: false, shift: false, alt: false, altOrCommand: true, code: "KeyH", keys: ["h"], displayKey: "H" }
};

const systemClipboardPasteBinding: CanvasShortcutBinding = {
  command: true,
  shift: true,
  alt: false,
  code: "KeyV",
  keys: ["v"],
  displayKey: "V"
};

const legacyDefaultBindings: Partial<CanvasShortcutBindings> = {
  "new-document": { command: true, shift: false, alt: false, code: "KeyN", keys: ["n"], displayKey: "N" },
  "new-note": { command: true, shift: true, alt: false, code: "KeyN", keys: ["n"], displayKey: "N" },
  "new-folder": { command: true, shift: true, alt: false, code: "KeyF", keys: ["f"], displayKey: "F" },
  "new-web-clip": { command: true, shift: true, alt: false, code: "KeyK", keys: ["k"], displayKey: "K" },
  "new-media": { command: true, shift: true, alt: false, code: "KeyM", keys: ["m"], displayKey: "M" },
  "new-label": { command: true, shift: true, alt: false, code: "KeyL", keys: ["l"], displayKey: "L" },
  duplicate: { command: true, shift: false, alt: false, code: "KeyD", keys: ["d"], displayKey: "D" },
  "bring-front": { command: true, shift: true, alt: false, code: "BracketRight", keys: ["]", "}"], displayKey: "]" },
  "send-back": { command: true, shift: true, alt: false, code: "BracketLeft", keys: ["[", "{"], displayKey: "[" },
  fit: { command: false, shift: true, alt: false, code: "Digit1", keys: ["1", "!"], displayKey: "1" },
  "text-bold": { command: true, shift: false, alt: false, code: "KeyB", keys: ["b"], displayKey: "B" },
  "text-italic": { command: true, shift: false, alt: false, code: "KeyI", keys: ["i"], displayKey: "I" },
  "text-underline": { command: true, shift: false, alt: false, code: "KeyU", keys: ["u"], displayKey: "U" },
  "text-heading-two": { command: true, shift: false, alt: true, code: "Digit2", keys: ["2"], displayKey: "2" },
  "text-bullet-list": { command: true, shift: true, alt: false, code: "Digit8", keys: ["8", "*"], displayKey: "8" },
  "text-ordered-list": { command: true, shift: true, alt: false, code: "Digit7", keys: ["7", "&"], displayKey: "7" },
  "text-checklist": { command: true, shift: true, alt: false, code: "Digit9", keys: ["9", "("], displayKey: "9" },
  "text-quote": { command: true, shift: true, alt: false, code: "KeyB", keys: ["b"], displayKey: "B" },
  "text-code-block": { command: true, shift: false, alt: true, code: "Digit0", keys: ["0"], displayKey: "0" },
  "text-highlight": { command: true, shift: true, alt: false, code: "KeyH", keys: ["h"], displayKey: "H" }
};

const shortcutOrder = CANVAS_SHORTCUT_ACTIONS.map(({ id }) => id);

const displayKeysByCode: Record<string, string> = {
  Backquote: "`", Minus: "-", Equal: "=", BracketLeft: "[", BracketRight: "]", Backslash: "\\",
  Semicolon: ";", Quote: "'", Comma: ",", Period: ".", Slash: "/", Space: "Space",
  Enter: "Enter", NumpadEnter: "Enter", Backspace: "Backspace", Delete: "Del", Insert: "Insert",
  Home: "Home", End: "End", PageUp: "Page Up", PageDown: "Page Down",
  ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→"
};

const electronKeysByDisplay: Record<string, string> = {
  "Del": "Delete", "Page Up": "PageUp", "Page Down": "PageDown",
  "↑": "Up", "↓": "Down", "←": "Left", "→": "Right"
};

export function defaultCanvasShortcutBindings(): CanvasShortcutBindings {
  return Object.fromEntries(shortcutOrder.map((id) => [id, cloneBinding(defaultBindings[id])])) as unknown as CanvasShortcutBindings;
}

export function detectShortcutPlatform(platform = typeof navigator === "undefined" ? "" : navigator.platform): ShortcutPlatform {
  return /mac|iphone|ipad|ipod/i.test(platform) ? "mac" : "other";
}

export function matchesCanvasShortcut(event: ShortcutEvent, id: CanvasShortcutId, platform = detectShortcutPlatform(), bindings = defaultBindings): boolean {
  return matchesShortcutBinding(event, bindings[id], platform);
}

export function isSystemClipboardPasteShortcut(event: ShortcutEvent, platform = detectShortcutPlatform()): boolean {
  return matchesShortcutBinding(event, systemClipboardPasteBinding, platform);
}

export function isSystemClipboardPasteBinding(binding: CanvasShortcutBinding, platform = detectShortcutPlatform()): boolean {
  return sameBinding(systemClipboardPasteBinding, binding, platform);
}

function matchesShortcutBinding(event: ShortcutEvent, binding: CanvasShortcutBinding, platform: ShortcutPlatform): boolean {
  const modifiers = resolvedModifiers(binding, platform);
  if (event.ctrlKey !== modifiers.control || event.metaKey !== modifiers.meta) return false;
  if (event.shiftKey !== modifiers.shift || event.altKey !== modifiers.alt) return false;
  if (binding.code && event.code) {
    if (event.code === binding.code) return true;
  }
  return binding.keys.includes(event.key.toLocaleLowerCase());
}

export function canvasShortcutForEvent(event: ShortcutEvent, platform = detectShortcutPlatform(), bindings = defaultBindings): CanvasShortcutId | null {
  return shortcutOrder.find((id) => matchesCanvasShortcut(event, id, platform, bindings)) ?? null;
}

export function canvasShortcutLabel(id: CanvasShortcutId, platform = detectShortcutPlatform(), bindings = defaultBindings): string {
  return shortcutLabel(bindings[id], platform);
}

export function systemClipboardPasteShortcutLabel(platform = detectShortcutPlatform()): string {
  return shortcutLabel(systemClipboardPasteBinding, platform);
}

function shortcutLabel(binding: CanvasShortcutBinding, platform: ShortcutPlatform): string {
  const key = platform === "mac" ? binding.macDisplayKey ?? binding.displayKey : binding.displayKey;
  const modifiers = resolvedModifiers(binding, platform);
  if (platform === "mac") return `${modifiers.control ? "⌃" : ""}${modifiers.shift ? "⇧" : ""}${modifiers.alt ? "⌥" : ""}${modifiers.meta ? "⌘" : ""}${key}`;
  return [modifiers.control ? "Ctrl" : "", modifiers.alt ? "Alt" : "", modifiers.shift ? "Shift" : "", key].filter(Boolean).join("+");
}

export function canvasShortcutAria(id: CanvasShortcutId, platform = detectShortcutPlatform(), bindings = defaultBindings): string {
  return shortcutAria(bindings[id], platform);
}

export function systemClipboardPasteShortcutAria(platform = detectShortcutPlatform()): string {
  return shortcutAria(systemClipboardPasteBinding, platform);
}

function shortcutAria(binding: CanvasShortcutBinding, platform: ShortcutPlatform): string {
  if (binding.ariaKeys) return binding.ariaKeys.join(" ");
  const modifiers = resolvedModifiers(binding, platform);
  return [modifiers.control ? "Control" : "", modifiers.meta ? "Meta" : "", modifiers.alt ? "Alt" : "", modifiers.shift ? "Shift" : "", ariaKey(binding.displayKey)]
    .filter(Boolean)
    .join("+");
}

export function canvasShortcutMenuProps(id: CanvasShortcutId, bindings = defaultBindings, platform = detectShortcutPlatform()) {
  return {
    shortcut: canvasShortcutLabel(id, platform, bindings),
    "aria-keyshortcuts": canvasShortcutAria(id, platform, bindings)
  } as const;
}

export function canvasShortcutBindingFromEvent(event: ShortcutEvent, platform = detectShortcutPlatform()): CanvasShortcutBinding | null {
  const key = event.key.toLocaleLowerCase();
  if (["alt", "altgraph", "control", "meta", "shift", "tab", "escape"].includes(key)) return null;
  const command = platform === "mac" ? event.metaKey : event.ctrlKey;
  const control = platform === "mac" && event.ctrlKey;
  if (platform === "mac" ? event.metaKey && event.ctrlKey : event.metaKey) return null;
  const displayKey = displayKeyForEvent(event);
  if (!displayKey) return null;
  const acceleratorKey = electronKey(displayKey);
  return {
    command,
    ...(control ? { control: true } : {}),
    shift: event.shiftKey,
    alt: event.altKey,
    code: event.code,
    keys: [key],
    displayKey,
    ...(acceleratorKey ? { electronKey: acceleratorKey } : {})
  };
}

export function canvasShortcutConflict(bindings: CanvasShortcutBindings, id: CanvasShortcutId, candidate: CanvasShortcutBinding, platform = detectShortcutPlatform()): CanvasShortcutId | null {
  return shortcutOrder.find((otherId) => otherId !== id && sameBinding(bindings[otherId], candidate, platform)) ?? null;
}

export function loadCanvasShortcutBindings(storage = typeof localStorage === "undefined" ? undefined : localStorage): CanvasShortcutBindings {
  const defaults = defaultCanvasShortcutBindings();
  const platform = detectShortcutPlatform();
  if (!storage) return defaults;
  try {
    const saved = JSON.parse(storage.getItem(CANVAS_SHORTCUT_STORAGE_KEY) ?? "null") as unknown;
    if (!saved || typeof saved !== "object") return defaults;
    for (const id of shortcutOrder) {
      const binding = (saved as Record<string, unknown>)[id];
      if (!isCanvasShortcutBinding(binding)) continue;
      if (isSystemClipboardPasteBinding(binding, platform)) continue;
      const legacyDefault = legacyDefaultBindings[id];
      if (legacyDefault && sameStoredBinding(legacyDefault, binding)) continue;
      const claimsNewDefault = shortcutOrder.some((otherId) => otherId !== id && sameBinding(defaultBindings[otherId], binding, platform));
      if (!claimsNewDefault) defaults[id] = cloneBinding(binding);
    }
  } catch { /* malformed shortcut preferences fall back to defaults */ }
  return defaults;
}

export function saveCanvasShortcutBindings(bindings: CanvasShortcutBindings, storage = typeof localStorage === "undefined" ? undefined : localStorage): void {
  storage?.setItem(CANVAS_SHORTCUT_STORAGE_KEY, JSON.stringify(bindings));
}

export function canvasShortcutAccelerators(bindings: CanvasShortcutBindings, platform = detectShortcutPlatform()): CanvasShortcutAccelerators {
  return Object.fromEntries(shortcutOrder.map((id) => {
    const binding = bindings[id];
    const key = binding.electronKey ?? electronKey(binding.displayKey);
    if (!key) return [id, null];
    const modifiers = resolvedModifiers(binding, platform);
    const command = binding.altOrCommand ? platform === "mac" ? "Command" : "" : binding.command ? "CommandOrControl" : "";
    return [id, [modifiers.control && !binding.command ? "Control" : "", command, modifiers.alt ? "Alt" : "", modifiers.shift ? "Shift" : "", key].filter(Boolean).join("+")];
  }));
}

function cloneBinding(binding: CanvasShortcutBinding): CanvasShortcutBinding {
  return { ...binding, keys: [...binding.keys], ...(binding.ariaKeys ? { ariaKeys: [...binding.ariaKeys] } : {}) };
}

function sameBinding(left: CanvasShortcutBinding, right: CanvasShortcutBinding, platform: ShortcutPlatform): boolean {
  const leftModifiers = resolvedModifiers(left, platform);
  const rightModifiers = resolvedModifiers(right, platform);
  return leftModifiers.control === rightModifiers.control && leftModifiers.meta === rightModifiers.meta
    && leftModifiers.shift === rightModifiers.shift && leftModifiers.alt === rightModifiers.alt
    && (left.code && right.code ? left.code === right.code : left.keys.some((key) => right.keys.includes(key)));
}

function sameStoredBinding(left: CanvasShortcutBinding, right: CanvasShortcutBinding): boolean {
  return left.command === right.command && Boolean(left.control) === Boolean(right.control)
    && left.shift === right.shift && left.alt === right.alt && Boolean(left.altOrCommand) === Boolean(right.altOrCommand)
    && (left.code && right.code ? left.code === right.code : left.keys.some((key) => right.keys.includes(key)));
}

function resolvedModifiers(binding: CanvasShortcutBinding, platform: ShortcutPlatform) {
  const altOrCommand = Boolean(binding.altOrCommand);
  return {
    control: Boolean(binding.control) || !altOrCommand && binding.command && platform === "other",
    meta: (altOrCommand || binding.command) && platform === "mac",
    alt: altOrCommand ? platform === "other" : binding.alt,
    shift: binding.shift
  };
}

function displayKeyForEvent(event: ShortcutEvent): string {
  if (/^Key[A-Z]$/.test(event.code)) return event.code.slice(3);
  if (/^Digit[0-9]$/.test(event.code)) return event.code.slice(5);
  if (/^F(?:[1-9]|1[0-9]|2[0-4])$/.test(event.code)) return event.code;
  if (/^Numpad[0-9]$/.test(event.code)) return `Num ${event.code.slice(6)}`;
  const mapped = displayKeysByCode[event.code];
  if (mapped) return mapped;
  if (event.key.length === 1) return event.key.toLocaleUpperCase();
  return event.key ? event.key.replace(/([a-z])([A-Z])/g, "$1 $2") : "";
}

function ariaKey(displayKey: string): string {
  return electronKeysByDisplay[displayKey] ?? displayKey.replaceAll(" ", "");
}

function electronKey(displayKey: string): string | undefined {
  const normalized = electronKeysByDisplay[displayKey] ?? displayKey.replaceAll(" ", "");
  if (/^[A-Z0-9]$/.test(normalized) || /^F(?:[1-9]|1[0-9]|2[0-4])$/.test(normalized)) return normalized;
  if (["`", "-", "=", "[", "]", "\\", ";", "'", ",", ".", "/", "Space", "Enter", "Backspace", "Delete", "Insert", "Home", "End", "PageUp", "PageDown", "Up", "Down", "Left", "Right"].includes(normalized)) return normalized;
  return undefined;
}

function isCanvasShortcutBinding(value: unknown): value is CanvasShortcutBinding {
  if (!value || typeof value !== "object") return false;
  const binding = value as Partial<CanvasShortcutBinding>;
  return typeof binding.command === "boolean" && typeof binding.shift === "boolean" && typeof binding.alt === "boolean"
    && (binding.control === undefined || typeof binding.control === "boolean")
    && (binding.altOrCommand === undefined || typeof binding.altOrCommand === "boolean")
    && !(binding.altOrCommand && (binding.command || binding.control || binding.alt))
    && typeof binding.code === "string" && binding.code.length <= 40
    && Array.isArray(binding.keys) && binding.keys.length > 0 && binding.keys.length <= 3 && binding.keys.every((key) => typeof key === "string" && key.length <= 24)
    && typeof binding.displayKey === "string" && binding.displayKey.length > 0 && binding.displayKey.length <= 24
    && (binding.macDisplayKey === undefined || typeof binding.macDisplayKey === "string")
    && (binding.ariaKeys === undefined || Array.isArray(binding.ariaKeys) && binding.ariaKeys.every((key) => typeof key === "string"))
    && (binding.electronKey === undefined || typeof binding.electronKey === "string");
}
